import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { affiliateAPI } from '../services/affiliateApi';
import { toast } from 'react-hot-toast';
import {
  LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import {
  CurrencyDollarIcon,
  UserPlusIcon,
  ArrowTrendingUpIcon,
  ClipboardDocumentIcon,
  LinkIcon,
  FunnelIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  BanknotesIcon,
  CursorArrowRaysIcon,
  Squares2X2Icon,
  ChartBarIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';

const CHART_COLORS = ['#ec4899', '#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

const AffiliateDashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('overview');
  
  // Data states
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [conversions, setConversions] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [affiliateLink, setAffiliateLink] = useState(null);
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);
  
  // Filter states
  const [trafficFilters, setTrafficFilters] = useState({
    startDate: '',
    endDate: '',
    sub1: '',
    sub2: '',
    sub3: '',
    country: '',
    groupBy: '',
  });
  
  const [conversionFilters, setConversionFilters] = useState({
    startDate: '',
    endDate: '',
    conversionType: '',
    isPaid: '',
  });
  
  // Reports state
  const [reports, setReports] = useState(null);
  const [reportFilters, setReportFilters] = useState({
    startDate: '',
    endDate: '',
    groupBy: 'date',
    sub1: '',
    sub2: '',
    sub3: '',
    country: '',
    os: '',
    browser: '',
    device: '',
  });
  const [reportSort, setReportSort] = useState({ column: 'label', direction: 'asc' });
  
  // Link builder state
  const [linkBuilder, setLinkBuilder] = useState({
    sub1: '',
    sub2: '',
    sub3: '',
    page: 'home',
  });
  const [builtLink, setBuiltLink] = useState(null);
  
  // Analytics period
  const [analyticsDays, setAnalyticsDays] = useState(30);

  // Check authentication and affiliate status
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/affiliate-dashboard');
      return;
    }
    
    checkAffiliateStatus();
  }, [isAuthenticated, navigate]);

  const checkAffiliateStatus = async () => {
    try {
      const response = await affiliateAPI.getStatus();
      if (!response.data.is_affiliate) {
        toast.error(t('affiliate.dashboard.messages.notApprovedAffiliate'));
        navigate('/affiliates');
        return;
      }
      
      // Load initial data
      await Promise.all([
        fetchStats(),
        fetchAnalytics(),
        fetchLink(),
      ]);
    } catch (error) {
      console.error('Error checking affiliate status:', error);
      if (error.response?.status === 403) {
        navigate('/affiliates');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await affiliateAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchAnalytics = async (days = analyticsDays) => {
    try {
      const response = await affiliateAPI.getAnalytics(days);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchLink = async () => {
    try {
      const response = await affiliateAPI.getLink(false);
      setAffiliateLink(response.data);
    } catch (error) {
      console.error('Error fetching link:', error);
    }
  };

  const fetchTraffic = async () => {
    setTabLoading(true);
    try {
      const response = await affiliateAPI.getTraffic(trafficFilters);
      setTraffic(response.data);
    } catch (error) {
      console.error('Error fetching traffic:', error);
    } finally {
      setTabLoading(false);
    }
  };

  const fetchConversions = async () => {
    setTabLoading(true);
    try {
      const response = await affiliateAPI.getConversions(conversionFilters);
      setConversions(response.data);
    } catch (error) {
      console.error('Error fetching conversions:', error);
    } finally {
      setTabLoading(false);
    }
  };

  const fetchPayouts = async () => {
    setTabLoading(true);
    try {
      const response = await affiliateAPI.getPayouts();
      setPayouts(response.data);
    } catch (error) {
      console.error('Error fetching payouts:', error);
    } finally {
      setTabLoading(false);
    }
  };

  const fetchReports = async () => {
    setTabLoading(true);
    try {
      const response = await affiliateAPI.getReports(reportFilters);
      setReports(response.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setTabLoading(false);
    }
  };

  // Handle report column sorting
  const handleReportSort = (column) => {
    setReportSort(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Get sorted report groups
  const getSortedReportGroups = () => {
    if (!reports?.groups) return [];
    
    const groups = [...reports.groups];
    if (!reportSort.column) return groups;
    
    return groups.sort((a, b) => {
      let aVal = a[reportSort.column];
      let bVal = b[reportSort.column];
      
      // Handle string vs number comparison
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (aVal < bVal) return reportSort.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return reportSort.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Export reports to CSV
  const exportReportsToCSV = () => {
    if (!reports?.groups || reports.groups.length === 0) return;
    
    const headers = [
      reportFilters.groupBy === 'date' ? 'Date' : 'Dimension',
      'Clicks',
      'Signups',
      'Upgrades',
      'Commission',
      'Click to Signup %',
      'Signup to Upgrade %',
      'Click to Upgrade %'
    ];
    
    const rows = getSortedReportGroups().map(group => [
      group.label,
      group.clicks || 0,
      group.signups || 0,
      group.upgrades || 0,
      group.commission || 0,
      group.click_to_signup_rate || 0,
      group.signup_to_upgrade_rate || 0,
      group.click_to_upgrade_rate || 0
    ]);
    
    // Add totals row
    if (reports.totals) {
      rows.push([
        'TOTAL',
        reports.totals.clicks || 0,
        reports.totals.signups || 0,
        reports.totals.upgrades || 0,
        reports.totals.commission || 0,
        reports.totals.click_to_signup_rate || 0,
        reports.totals.signup_to_upgrade_rate || 0,
        reports.totals.click_to_upgrade_rate || 0
      ]);
    }
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `affiliate-report-${reportFilters.groupBy}-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(t('affiliate.dashboard.reports.exportSuccess'));
  };

  // Load tab data when tab changes
  useEffect(() => {
    if (loading) return;
    
    switch (activeTab) {
      case 'traffic':
        if (!traffic) fetchTraffic();
        break;
      case 'conversions':
        if (!conversions) fetchConversions();
        break;
      case 'payouts':
        if (!payouts) fetchPayouts();
        break;
      case 'reports':
        if (!reports) fetchReports();
        break;
      default:
        break;
    }
  }, [activeTab, loading]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(t('affiliate.dashboard.messages.copiedToClipboard'));
  };

  const buildCustomLink = async () => {
    try {
      const response = await affiliateAPI.buildLink(linkBuilder);
      setBuiltLink(response.data);
    } catch (error) {
      console.error('Error building link:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-400 mx-auto"></div>
          <p className="mt-4 text-gray-300">{t('affiliate.dashboard.loadingDashboard')}</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: t('affiliate.dashboard.tabs.overview'), icon: Squares2X2Icon },
    { id: 'reports', label: t('affiliate.dashboard.tabs.reports'), icon: ChartBarIcon },
    { id: 'traffic', label: t('affiliate.dashboard.tabs.traffic'), icon: CursorArrowRaysIcon },
    { id: 'conversions', label: t('affiliate.dashboard.tabs.conversions'), icon: UserPlusIcon },
    { id: 'payouts', label: t('affiliate.dashboard.tabs.payouts'), icon: BanknotesIcon },
    { id: 'links', label: t('affiliate.dashboard.tabs.linkBuilder'), icon: LinkIcon },
  ];

  return (
    <div className="min-h-screen pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white">{t('affiliate.dashboard.title')}</h1>
              <p className="text-gray-300 mt-1">{t('affiliate.dashboard.subtitle')}</p>
            </div>
            <div className="flex items-center space-x-4">
              {affiliateLink && (
                <div className="bg-white/10 backdrop-blur-sm rounded-xl px-5 py-3 border border-white/20">
                  <p className="text-xs text-gray-400">{t('affiliate.dashboard.yourCode')}</p>
                  <p className="font-mono font-bold text-white text-lg">{affiliateLink.affiliate_code}</p>
                </div>
              )}
              <button
                onClick={() => fetchStats().then(fetchAnalytics)}
                className="p-3 bg-white/10 hover:bg-white/20 rounded-xl transition-colors border border-white/20"
                title="Refresh"
              >
                <ArrowPathIcon className="h-5 w-5 text-white" />
              </button>
            </div>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="mb-8">
          <div className="flex space-x-2 overflow-x-auto pb-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-5 py-3 text-sm font-medium rounded-xl transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg'
                    : 'bg-white/10 text-gray-300 hover:text-white hover:bg-white/20 border border-white/10'
                }`}
              >
                <tab.icon className="h-5 w-5 mr-2" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div>
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                icon={CursorArrowRaysIcon}
                label={t('affiliate.dashboard.stats.totalClicks')}
                value={stats?.total_visits?.toLocaleString() || '0'}
                color="blue"
              />
              <StatCard
                icon={UserPlusIcon}
                label={t('affiliate.dashboard.stats.signups')}
                value={stats?.total_signups?.toLocaleString() || '0'}
                subtitle={`${stats?.click_to_signup_rate || 0}% ${t('affiliate.dashboard.stats.conversion')}`}
                color="green"
              />
              <StatCard
                icon={ArrowTrendingUpIcon}
                label={t('affiliate.dashboard.stats.upgrades')}
                value={stats?.total_upgrades?.toLocaleString() || '0'}
                subtitle={`${stats?.signup_to_upgrade_rate || 0}% ${t('affiliate.dashboard.stats.ofSignups')}`}
                color="purple"
              />
              <StatCard
                icon={CurrencyDollarIcon}
                label={t('affiliate.dashboard.stats.totalEarnings')}
                value={formatCurrency(stats?.total_commission)}
                subtitle={`${formatCurrency(stats?.pending_commission)} ${t('affiliate.dashboard.stats.pending')}`}
                color="pink"
              />
            </div>

            {/* Commission Structure */}
            <div className="bg-gradient-to-r from-indigo-500/20 to-purple-600/20 backdrop-blur-lg rounded-xl border border-indigo-400/30 p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                <span className="mr-2">💰</span> {t('affiliate.dashboard.commission.title')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Signup Commission */}
                <div className="bg-white/10 rounded-lg p-4 border border-white/10">
                  <div className="text-gray-400 text-sm mb-2">{t('affiliate.dashboard.commission.signupCommission')}</div>
                  <div className="text-white font-semibold text-lg">
                    {stats?.signup_payout_type === 'revshare' ? (
                      <span className="text-purple-400">{stats?.signup_revshare_percent || 0}% {t('affiliate.dashboard.commission.ofFirstPayment')}</span>
                    ) : (
                      <span className="text-green-400">${stats?.signup_commission_rate?.toFixed(2) || '0.00'} {t('affiliate.dashboard.commission.perSignup')}</span>
                    )}
                  </div>
                  <p className="text-gray-400 text-xs mt-2">
                    {t('affiliate.dashboard.commission.signupEarned')}
                  </p>
                </div>

                {/* Upgrade Commission */}
                <div className="bg-white/10 rounded-lg p-4 border border-white/10">
                  <div className="text-gray-400 text-sm mb-2">{t('affiliate.dashboard.commission.upgradeCommission')}</div>
                  {stats?.upgrade_payout_type === 'revshare' ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-300 text-sm">{t('affiliate.dashboard.commission.monthlyPlans')}</span>
                        <span className="text-blue-400 font-semibold">
                          {stats?.monthly_revshare_percent || 0}% 
                          {stats?.revshare_months ? ` ${t('affiliate.dashboard.commission.forMonths', { months: stats?.revshare_months })}` : ` ${t('affiliate.dashboard.commission.recurring')}`}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-300 text-sm">{t('affiliate.dashboard.commission.annualPlans')}</span>
                        <span className="text-green-400 font-semibold">{stats?.annual_revshare_percent || 0}% {t('affiliate.dashboard.commission.oneTime')}</span>
                      </div>
                      <p className="text-gray-400 text-xs mt-2">
                        {t('affiliate.dashboard.commission.revenueShare')}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="text-white font-semibold text-lg">
                        <span className="text-green-400">${stats?.upgrade_commission_rate?.toFixed(2) || '0.00'} {t('affiliate.dashboard.commission.perUpgrade')}</span>
                      </div>
                      <p className="text-gray-400 text-xs mt-2">
                        {t('affiliate.dashboard.commission.fixedPayout')}
                      </p>
                    </>
                  )}
                </div>
              </div>

              {/* Explanation */}
              <div className="mt-4 p-3 bg-white/5 rounded-lg border border-white/10">
                <p className="text-gray-300 text-sm">
                  <span className="text-yellow-400 font-medium">💡 {t('affiliate.dashboard.commission.howItWorks')}</span> {t('affiliate.dashboard.commission.howItWorksDesc')}
                  {stats?.upgrade_payout_type === 'revshare' && stats?.revshare_months && (
                    <> {t('affiliate.dashboard.commission.monthlyRecurring', { percent: stats?.monthly_revshare_percent, months: stats?.revshare_months })}</>
                  )}
                </p>
              </div>
            </div>

            {/* Analytics Period Selector */}
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">{t('affiliate.dashboard.analytics.title')}</h2>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-400">{t('affiliate.dashboard.analytics.period')}</span>
                <select
                  value={analyticsDays}
                  onChange={(e) => {
                    setAnalyticsDays(Number(e.target.value));
                    fetchAnalytics(Number(e.target.value));
                  }}
                  className="bg-white/10 border border-white/20 text-white rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                >
                  <option value={7} className="bg-gray-900">{t('affiliate.dashboard.analytics.last7Days')}</option>
                  <option value={30} className="bg-gray-900">{t('affiliate.dashboard.analytics.last30Days')}</option>
                  <option value={90} className="bg-gray-900">{t('affiliate.dashboard.analytics.last90Days')}</option>
                </select>
              </div>
            </div>

            {/* Charts */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Traffic Chart */}
              <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 p-6">
                <h3 className="text-lg font-semibold text-white mb-4">{t('affiliate.dashboard.analytics.trafficOverTime')}</h3>
                {analytics?.chart_data && (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={analytics.chart_data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        tick={{ fontSize: 12, fill: '#9ca3af' }}
                        stroke="#4b5563"
                      />
                      <YAxis tick={{ fontSize: 12, fill: '#9ca3af' }} stroke="#4b5563" />
                      <Tooltip 
                        labelFormatter={(date) => new Date(date).toLocaleDateString()}
                        contentStyle={{ backgroundColor: '#1f2937', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', color: '#fff' }}
                      />
                      <Legend wrapperStyle={{ color: '#9ca3af' }} />
                      <Line type="monotone" dataKey="clicks" stroke="#3b82f6" strokeWidth={2} dot={false} name={t('affiliate.dashboard.analytics.clicks')} />
                      <Line type="monotone" dataKey="signups" stroke="#10b981" strokeWidth={2} dot={false} name={t('affiliate.dashboard.analytics.signups')} />
                      <Line type="monotone" dataKey="upgrades" stroke="#a855f7" strokeWidth={2} dot={false} name={t('affiliate.dashboard.analytics.upgrades')} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Source Breakdown */}
              <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 p-6">
                <h3 className="text-lg font-semibold text-white mb-4">{t('affiliate.dashboard.analytics.trafficSources')}</h3>
                {analytics?.source_breakdown && analytics.source_breakdown.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={analytics.source_breakdown}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        nameKey="name"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      >
                        {analytics.source_breakdown.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: '#1f2937', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', color: '#fff' }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-gray-400">
                    {t('affiliate.dashboard.analytics.noTrafficData')}
                  </div>
                )}
              </div>
            </div>

            {/* Conversion Funnel */}
            {analytics?.funnel && (
              <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 p-6">
                <h3 className="text-lg font-semibold text-white mb-4">{t('affiliate.dashboard.funnel.title')}</h3>
                <div className="flex items-center justify-between">
                  <FunnelStep label={t('affiliate.dashboard.funnel.clicks')} value={analytics.funnel.clicks} color="blue" />
                  <FunnelArrow rate={`${analytics.funnel.click_to_signup_rate}%`} />
                  <FunnelStep label={t('affiliate.dashboard.funnel.signups')} value={analytics.funnel.signups} color="green" />
                  <FunnelArrow rate={`${analytics.funnel.signup_to_upgrade_rate}%`} />
                  <FunnelStep label={t('affiliate.dashboard.funnel.upgrades')} value={analytics.funnel.upgrades} color="purple" />
                </div>
              </div>
            )}

            {/* Quick Link Copy */}
            {affiliateLink && (
              <div className="bg-gradient-to-r from-pink-500/20 to-purple-600/20 backdrop-blur-lg rounded-xl border border-pink-500/30 p-6">
                <h3 className="text-lg font-semibold text-white mb-2">{t('affiliate.dashboard.affiliateLink.title')}</h3>
                <div className="flex items-center bg-white/10 rounded-lg p-3 border border-white/10">
                  <input
                    type="text"
                    value={affiliateLink.link}
                    readOnly
                    className="flex-1 bg-transparent text-white font-mono text-sm focus:outline-none"
                  />
                  <button
                    onClick={() => copyToClipboard(affiliateLink.link)}
                    className="ml-2 p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-white"
                  >
                    <ClipboardDocumentIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">{t('affiliate.dashboard.reports.title')}</h3>
                <div className="flex gap-2">
                  <button
                    onClick={exportReportsToCSV}
                    disabled={!reports?.groups || reports.groups.length === 0}
                    className="px-4 py-2 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 disabled:opacity-50 flex items-center"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                    {t('affiliate.dashboard.reports.exportCSV')}
                  </button>
                  <button
                    onClick={fetchReports}
                    disabled={tabLoading}
                    className="px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-lg hover:from-pink-600 hover:to-purple-700 disabled:opacity-50 flex items-center"
                  >
                    {tabLoading ? (
                      <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <ChartBarIcon className="h-4 w-4 mr-2" />
                    )}
                    {t('affiliate.dashboard.reports.generateReport')}
                  </button>
                </div>
              </div>
              
              {/* Date and Group By */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">{t('affiliate.dashboard.reports.startDate')}</label>
                  <input
                    type="date"
                    value={reportFilters.startDate}
                    onChange={(e) => setReportFilters({ ...reportFilters, startDate: e.target.value })}
                    className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-pink-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">{t('affiliate.dashboard.reports.endDate')}</label>
                  <input
                    type="date"
                    value={reportFilters.endDate}
                    onChange={(e) => setReportFilters({ ...reportFilters, endDate: e.target.value })}
                    className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-pink-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-1">{t('affiliate.dashboard.reports.groupBy')}</label>
                  <select
                    value={reportFilters.groupBy}
                    onChange={(e) => setReportFilters({ ...reportFilters, groupBy: e.target.value })}
                    className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-pink-500"
                  >
                    <option value="date" className="bg-gray-900">{t('affiliate.dashboard.reports.groupByDate')}</option>
                    <option value="country" className="bg-gray-900">{t('affiliate.dashboard.reports.groupByCountry')}</option>
                    <option value="os" className="bg-gray-900">{t('affiliate.dashboard.reports.groupByOS')}</option>
                    <option value="browser" className="bg-gray-900">{t('affiliate.dashboard.reports.groupByBrowser')}</option>
                    <option value="device" className="bg-gray-900">{t('affiliate.dashboard.reports.groupByDevice')}</option>
                    <option value="sub1" className="bg-gray-900">{t('affiliate.dashboard.linkBuilder.sub1')}</option>
                    <option value="sub2" className="bg-gray-900">{t('affiliate.dashboard.linkBuilder.sub2')}</option>
                    <option value="sub3" className="bg-gray-900">{t('affiliate.dashboard.linkBuilder.sub3')}</option>
                  </select>
                </div>
              </div>
              
              {/* Attribute Filters */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">{t('affiliate.dashboard.linkBuilder.sub1')}</label>
                  <input
                    type="text"
                    value={reportFilters.sub1}
                    onChange={(e) => setReportFilters({ ...reportFilters, sub1: e.target.value })}
                    placeholder={t('affiliate.dashboard.reports.filterPlaceholder')}
                    className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-pink-500 placeholder-gray-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">{t('affiliate.dashboard.linkBuilder.sub2')}</label>
                  <input
                    type="text"
                    value={reportFilters.sub2}
                    onChange={(e) => setReportFilters({ ...reportFilters, sub2: e.target.value })}
                    placeholder={t('affiliate.dashboard.reports.filterPlaceholder')}
                    className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-pink-500 placeholder-gray-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">{t('affiliate.dashboard.linkBuilder.sub3')}</label>
                  <input
                    type="text"
                    value={reportFilters.sub3}
                    onChange={(e) => setReportFilters({ ...reportFilters, sub3: e.target.value })}
                    placeholder={t('affiliate.dashboard.reports.filterPlaceholder')}
                    className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-pink-500 placeholder-gray-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">{t('affiliate.dashboard.reports.groupByCountry')}</label>
                  <input
                    type="text"
                    value={reportFilters.country}
                    onChange={(e) => setReportFilters({ ...reportFilters, country: e.target.value })}
                    placeholder="US, GB..."
                    className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-pink-500 placeholder-gray-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">{t('affiliate.dashboard.reports.groupByOS')}</label>
                  <input
                    type="text"
                    value={reportFilters.os}
                    onChange={(e) => setReportFilters({ ...reportFilters, os: e.target.value })}
                    placeholder="iOS, Android..."
                    className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-pink-500 placeholder-gray-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">{t('affiliate.dashboard.reports.groupByBrowser')}</label>
                  <input
                    type="text"
                    value={reportFilters.browser}
                    onChange={(e) => setReportFilters({ ...reportFilters, browser: e.target.value })}
                    placeholder="Chrome, Safari..."
                    className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-pink-500 placeholder-gray-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">{t('affiliate.dashboard.reports.groupByDevice')}</label>
                  <input
                    type="text"
                    value={reportFilters.device}
                    onChange={(e) => setReportFilters({ ...reportFilters, device: e.target.value })}
                    placeholder="mobile, desktop..."
                    className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-pink-500 placeholder-gray-500 text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Totals Summary */}
            {reports?.totals && (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                <div className="bg-blue-500/20 backdrop-blur-lg rounded-xl border border-blue-500/30 p-4">
                  <p className="text-xs text-blue-300">{t('affiliate.dashboard.reports.totalClicks')}</p>
                  <p className="text-xl font-bold text-white">{reports.totals.clicks?.toLocaleString() || 0}</p>
                </div>
                <div className="bg-green-500/20 backdrop-blur-lg rounded-xl border border-green-500/30 p-4">
                  <p className="text-xs text-green-300">{t('affiliate.dashboard.reports.totalSignups')}</p>
                  <p className="text-xl font-bold text-white">{reports.totals.signups?.toLocaleString() || 0}</p>
                </div>
                <div className="bg-purple-500/20 backdrop-blur-lg rounded-xl border border-purple-500/30 p-4">
                  <p className="text-xs text-purple-300">{t('affiliate.dashboard.reports.totalUpgrades')}</p>
                  <p className="text-xl font-bold text-white">{reports.totals.upgrades?.toLocaleString() || 0}</p>
                </div>
                <div className="bg-pink-500/20 backdrop-blur-lg rounded-xl border border-pink-500/30 p-4">
                  <p className="text-xs text-pink-300">{t('affiliate.dashboard.reports.totalCommission')}</p>
                  <p className="text-xl font-bold text-white">{formatCurrency(reports.totals.commission)}</p>
                </div>
                <div className="bg-cyan-500/20 backdrop-blur-lg rounded-xl border border-cyan-500/30 p-4">
                  <p className="text-xs text-cyan-300">{t('affiliate.dashboard.reports.clickToSignup')}</p>
                  <p className="text-xl font-bold text-white">{reports.totals.click_to_signup_rate || 0}%</p>
                </div>
                <div className="bg-amber-500/20 backdrop-blur-lg rounded-xl border border-amber-500/30 p-4">
                  <p className="text-xs text-amber-300">{t('affiliate.dashboard.reports.signupToUpgrade')}</p>
                  <p className="text-xl font-bold text-white">{reports.totals.signup_to_upgrade_rate || 0}%</p>
                </div>
                <div className="bg-indigo-500/20 backdrop-blur-lg rounded-xl border border-indigo-500/30 p-4">
                  <p className="text-xs text-indigo-300">{t('affiliate.dashboard.reports.clickToUpgrade')}</p>
                  <p className="text-xl font-bold text-white">{reports.totals.click_to_upgrade_rate || 0}%</p>
                </div>
              </div>
            )}

            {/* Report Data Table */}
            <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 overflow-hidden">
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white">{t('affiliate.dashboard.reports.funnelBreakdown')}</h3>
                  <span className="text-sm text-gray-400">
                    {t('affiliate.dashboard.reports.groupedBy')}: {reportFilters.groupBy}
                  </span>
                </div>
              </div>
              
              {tabLoading ? (
                <div className="p-12 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-400 mx-auto"></div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-white/10">
                    <thead className="bg-white/5">
                      <tr>
                        <th 
                          onClick={() => handleReportSort('label')}
                          className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                        >
                          <div className="flex items-center gap-1">
                            {reportFilters.groupBy === 'date' ? t('affiliate.dashboard.reports.date') : t('affiliate.dashboard.reports.dimension')}
                            {reportSort.column === 'label' && (reportSort.direction === 'asc' ? <ArrowUpIcon className="h-3 w-3" /> : <ArrowDownIcon className="h-3 w-3" />)}
                          </div>
                        </th>
                        <th 
                          onClick={() => handleReportSort('clicks')}
                          className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                        >
                          <div className="flex items-center justify-end gap-1">
                            {t('affiliate.dashboard.reports.clicks')}
                            {reportSort.column === 'clicks' && (reportSort.direction === 'asc' ? <ArrowUpIcon className="h-3 w-3" /> : <ArrowDownIcon className="h-3 w-3" />)}
                          </div>
                        </th>
                        <th 
                          onClick={() => handleReportSort('signups')}
                          className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                        >
                          <div className="flex items-center justify-end gap-1">
                            {t('affiliate.dashboard.reports.signups')}
                            {reportSort.column === 'signups' && (reportSort.direction === 'asc' ? <ArrowUpIcon className="h-3 w-3" /> : <ArrowDownIcon className="h-3 w-3" />)}
                          </div>
                        </th>
                        <th 
                          onClick={() => handleReportSort('upgrades')}
                          className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                        >
                          <div className="flex items-center justify-end gap-1">
                            {t('affiliate.dashboard.reports.upgrades')}
                            {reportSort.column === 'upgrades' && (reportSort.direction === 'asc' ? <ArrowUpIcon className="h-3 w-3" /> : <ArrowDownIcon className="h-3 w-3" />)}
                          </div>
                        </th>
                        <th 
                          onClick={() => handleReportSort('commission')}
                          className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                        >
                          <div className="flex items-center justify-end gap-1">
                            {t('affiliate.dashboard.reports.commission')}
                            {reportSort.column === 'commission' && (reportSort.direction === 'asc' ? <ArrowUpIcon className="h-3 w-3" /> : <ArrowDownIcon className="h-3 w-3" />)}
                          </div>
                        </th>
                        <th 
                          onClick={() => handleReportSort('click_to_signup_rate')}
                          className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                        >
                          <div className="flex items-center justify-end gap-1">
                            {t('affiliate.dashboard.reports.clickToSignupShort')}
                            {reportSort.column === 'click_to_signup_rate' && (reportSort.direction === 'asc' ? <ArrowUpIcon className="h-3 w-3" /> : <ArrowDownIcon className="h-3 w-3" />)}
                          </div>
                        </th>
                        <th 
                          onClick={() => handleReportSort('signup_to_upgrade_rate')}
                          className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                        >
                          <div className="flex items-center justify-end gap-1">
                            {t('affiliate.dashboard.reports.signupToUpgradeShort')}
                            {reportSort.column === 'signup_to_upgrade_rate' && (reportSort.direction === 'asc' ? <ArrowUpIcon className="h-3 w-3" /> : <ArrowDownIcon className="h-3 w-3" />)}
                          </div>
                        </th>
                        <th 
                          onClick={() => handleReportSort('click_to_upgrade_rate')}
                          className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                        >
                          <div className="flex items-center justify-end gap-1">
                            {t('affiliate.dashboard.reports.clickToUpgradeShort')}
                            {reportSort.column === 'click_to_upgrade_rate' && (reportSort.direction === 'asc' ? <ArrowUpIcon className="h-3 w-3" /> : <ArrowDownIcon className="h-3 w-3" />)}
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                      {getSortedReportGroups().map((group, index) => (
                        <tr key={index} className="hover:bg-white/5">
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-white">
                            {group.label}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-right text-blue-400">
                            {group.clicks?.toLocaleString() || 0}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-right text-green-400">
                            {group.signups?.toLocaleString() || 0}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-right text-purple-400">
                            {group.upgrades?.toLocaleString() || 0}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-right text-pink-400 font-medium">
                            {formatCurrency(group.commission)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-right text-gray-300">
                            {group.click_to_signup_rate || 0}%
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-right text-gray-300">
                            {group.signup_to_upgrade_rate || 0}%
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-right text-gray-300">
                            {group.click_to_upgrade_rate || 0}%
                          </td>
                        </tr>
                      ))}
                      {(!reports?.groups || reports.groups.length === 0) && (
                        <tr>
                          <td colSpan="8" className="px-4 py-12 text-center text-gray-400">
                            {t('affiliate.dashboard.reports.noData')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Traffic Tab */}
        {activeTab === 'traffic' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">{t('affiliate.dashboard.traffic.filters')}</h3>
                <button
                  onClick={fetchTraffic}
                  disabled={tabLoading}
                  className="px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-lg hover:from-pink-600 hover:to-purple-700 disabled:opacity-50 flex items-center"
                >
                  {tabLoading ? (
                    <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <FunnelIcon className="h-4 w-4 mr-2" />
                  )}
                  {t('affiliate.dashboard.traffic.applyFilters')}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">{t('affiliate.dashboard.traffic.startDate')}</label>
                  <input
                    type="date"
                    value={trafficFilters.startDate}
                    onChange={(e) => setTrafficFilters({ ...trafficFilters, startDate: e.target.value })}
                    className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-pink-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">{t('affiliate.dashboard.traffic.endDate')}</label>
                  <input
                    type="date"
                    value={trafficFilters.endDate}
                    onChange={(e) => setTrafficFilters({ ...trafficFilters, endDate: e.target.value })}
                    className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-pink-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">{t('affiliate.dashboard.linkBuilder.sub1')}</label>
                  <input
                    type="text"
                    value={trafficFilters.sub1}
                    onChange={(e) => setTrafficFilters({ ...trafficFilters, sub1: e.target.value })}
                    placeholder="e.g., twitter"
                    className="w-full bg-white/10 border border-white/20 text-white placeholder-gray-400 rounded-lg px-3 py-2 focus:ring-2 focus:ring-pink-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">{t('affiliate.dashboard.linkBuilder.sub2')}</label>
                  <input
                    type="text"
                    value={trafficFilters.sub2}
                    onChange={(e) => setTrafficFilters({ ...trafficFilters, sub2: e.target.value })}
                    placeholder="e.g., banner"
                    className="w-full bg-white/10 border border-white/20 text-white placeholder-gray-400 rounded-lg px-3 py-2 focus:ring-2 focus:ring-pink-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">{t('affiliate.dashboard.linkBuilder.sub3')}</label>
                  <input
                    type="text"
                    value={trafficFilters.sub3}
                    onChange={(e) => setTrafficFilters({ ...trafficFilters, sub3: e.target.value })}
                    placeholder="e.g., ad1"
                    className="w-full bg-white/10 border border-white/20 text-white placeholder-gray-400 rounded-lg px-3 py-2 focus:ring-2 focus:ring-pink-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">{t('affiliate.dashboard.traffic.country')}</label>
                  <input
                    type="text"
                    value={trafficFilters.country}
                    onChange={(e) => setTrafficFilters({ ...trafficFilters, country: e.target.value.toUpperCase() })}
                    placeholder="e.g., US, IL"
                    maxLength={2}
                    className="w-full bg-white/10 border border-white/20 text-white placeholder-gray-400 rounded-lg px-3 py-2 focus:ring-2 focus:ring-pink-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">{t('affiliate.dashboard.traffic.groupBy')}</label>
                  <select
                    value={trafficFilters.groupBy}
                    onChange={(e) => setTrafficFilters({ ...trafficFilters, groupBy: e.target.value })}
                    className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-pink-500"
                  >
                    <option value="" className="bg-gray-900">{t('affiliate.dashboard.traffic.noGrouping')}</option>
                    <option value="day" className="bg-gray-900">{t('affiliate.dashboard.traffic.day')}</option>
                    <option value="week" className="bg-gray-900">{t('affiliate.dashboard.traffic.week')}</option>
                    <option value="month" className="bg-gray-900">{t('affiliate.dashboard.traffic.month')}</option>
                    <option value="sub1" className="bg-gray-900">{t('affiliate.dashboard.linkBuilder.sub1')}</option>
                    <option value="sub2" className="bg-gray-900">{t('affiliate.dashboard.linkBuilder.sub2')}</option>
                    <option value="sub3" className="bg-gray-900">{t('affiliate.dashboard.linkBuilder.sub3')}</option>
                    <option value="country" className="bg-gray-900">{t('affiliate.dashboard.traffic.country')}</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Traffic Data */}
            <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 overflow-hidden">
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white">{t('affiliate.dashboard.traffic.trafficLog')}</h3>
                  <span className="text-sm text-gray-400">
                    {traffic?.total?.toLocaleString() || 0} {t('affiliate.dashboard.traffic.totalVisits')}
                  </span>
                </div>
              </div>
              
              {tabLoading ? (
                <div className="p-12 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-400 mx-auto"></div>
                </div>
              ) : traffic?.grouped ? (
                // Grouped view
                <div className="p-6">
                  <div className="space-y-3">
                    {traffic.groups?.map((group, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10">
                        <span className="font-medium text-white">{group.label}</span>
                        <span className="text-pink-400 font-semibold">{group.count.toLocaleString()} {t('affiliate.dashboard.traffic.clicks')}</span>
                      </div>
                    ))}
                    {traffic.groups?.length === 0 && (
                      <p className="text-center text-gray-400 py-8">{t('affiliate.dashboard.traffic.noTrafficData')}</p>
                    )}
                  </div>
                </div>
              ) : (
                // Table view
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-white/10">
                    <thead className="bg-white/5">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">{t('affiliate.dashboard.traffic.date')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">{t('affiliate.dashboard.linkBuilder.sub1')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">{t('affiliate.dashboard.linkBuilder.sub2')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">{t('affiliate.dashboard.linkBuilder.sub3')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">{t('affiliate.dashboard.traffic.country')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">{t('affiliate.dashboard.traffic.browser')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">{t('affiliate.dashboard.traffic.os')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">{t('affiliate.dashboard.traffic.device')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">{t('affiliate.dashboard.traffic.referrer')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                      {traffic?.visits?.map((visit) => (
                        <tr key={visit.id} className="hover:bg-white/5">
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-white">
                            {formatDateTime(visit.created_at)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                            {visit.sub1 || <span className="text-gray-500">-</span>}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                            {visit.sub2 || <span className="text-gray-500">-</span>}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                            {visit.sub3 || <span className="text-gray-500">-</span>}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                            {visit.country || <span className="text-gray-500">-</span>}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                            {visit.browser || <span className="text-gray-500">-</span>}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                            {visit.operating_system || <span className="text-gray-500">-</span>}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                            {visit.device_type || <span className="text-gray-500">-</span>}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-400 max-w-xs truncate" title={visit.referrer}>
                            {visit.referrer ? (
                              <span className="text-blue-400">
                                {(() => {
                                  try {
                                    return new URL(visit.referrer).hostname;
                                  } catch {
                                    return visit.referrer.substring(0, 30);
                                  }
                                })()}
                              </span>
                            ) : (
                              <span className="text-gray-500">{t('affiliate.dashboard.traffic.direct')}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                      {traffic?.visits?.length === 0 && (
                        <tr>
                          <td colSpan="8" className="px-4 py-12 text-center text-gray-400">
                            {t('affiliate.dashboard.traffic.noTrafficData')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Conversions Tab */}
        {activeTab === 'conversions' && (
          <div className="space-y-6">
            {/* Summary Cards */}
            {conversions?.summary && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 p-6">
                  <p className="text-sm text-gray-400">{t('affiliate.dashboard.conversions.totalCommission')}</p>
                  <p className="text-2xl font-bold text-white">{formatCurrency(conversions.summary.total_commission)}</p>
                </div>
                <div className="bg-green-500/20 backdrop-blur-lg rounded-xl border border-green-500/30 p-6">
                  <p className="text-sm text-green-300">{t('affiliate.dashboard.conversions.paid')}</p>
                  <p className="text-2xl font-bold text-green-400">{formatCurrency(conversions.summary.paid_commission)}</p>
                </div>
                <div className="bg-amber-500/20 backdrop-blur-lg rounded-xl border border-amber-500/30 p-6">
                  <p className="text-sm text-amber-300">{t('affiliate.dashboard.conversions.pending')}</p>
                  <p className="text-2xl font-bold text-amber-400">{formatCurrency(conversions.summary.pending_commission)}</p>
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">{t('affiliate.dashboard.conversions.filters')}</h3>
                <button
                  onClick={fetchConversions}
                  disabled={tabLoading}
                  className="px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-lg hover:from-pink-600 hover:to-purple-700 disabled:opacity-50 flex items-center"
                >
                  {tabLoading ? (
                    <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <FunnelIcon className="h-4 w-4 mr-2" />
                  )}
                  {t('affiliate.dashboard.conversions.applyFilters')}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">{t('affiliate.dashboard.conversions.startDate')}</label>
                  <input
                    type="date"
                    value={conversionFilters.startDate}
                    onChange={(e) => setConversionFilters({ ...conversionFilters, startDate: e.target.value })}
                    className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-pink-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">{t('affiliate.dashboard.conversions.endDate')}</label>
                  <input
                    type="date"
                    value={conversionFilters.endDate}
                    onChange={(e) => setConversionFilters({ ...conversionFilters, endDate: e.target.value })}
                    className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-pink-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">{t('affiliate.dashboard.conversions.type')}</label>
                  <select
                    value={conversionFilters.conversionType}
                    onChange={(e) => setConversionFilters({ ...conversionFilters, conversionType: e.target.value })}
                    className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-pink-500"
                  >
                    <option value="" className="bg-gray-900">{t('affiliate.dashboard.conversions.allTypes')}</option>
                    <option value="signup" className="bg-gray-900">{t('affiliate.dashboard.conversions.signupsType')}</option>
                    <option value="upgrade" className="bg-gray-900">{t('affiliate.dashboard.conversions.upgradesType')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">{t('affiliate.dashboard.conversions.status')}</label>
                  <select
                    value={conversionFilters.isPaid}
                    onChange={(e) => setConversionFilters({ ...conversionFilters, isPaid: e.target.value })}
                    className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-pink-500"
                  >
                    <option value="" className="bg-gray-900">{t('affiliate.dashboard.conversions.all')}</option>
                    <option value="true" className="bg-gray-900">{t('affiliate.dashboard.conversions.paidStatus')}</option>
                    <option value="false" className="bg-gray-900">{t('affiliate.dashboard.conversions.pendingStatus')}</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Conversions Table */}
            <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 overflow-hidden">
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white">{t('affiliate.dashboard.conversions.conversionsTitle')}</h3>
                  <span className="text-sm text-gray-400">
                    {conversions?.total?.toLocaleString() || 0} {t('affiliate.dashboard.conversions.total')}
                  </span>
                </div>
              </div>
              
              {tabLoading ? (
                <div className="p-12 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-400 mx-auto"></div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-white/10">
                    <thead className="bg-white/5">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">{t('affiliate.dashboard.conversions.date')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">{t('affiliate.dashboard.conversions.user')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">{t('affiliate.dashboard.conversions.type')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">{t('affiliate.dashboard.conversions.commission')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">{t('affiliate.dashboard.conversions.status')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                      {conversions?.conversions?.map((conv) => (
                        <tr key={conv.id} className="hover:bg-white/5">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                            {formatDate(conv.created_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-300">
                            {conv.converted_user_email}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              conv.conversion_type === 'signup' 
                                ? 'bg-blue-500/20 text-blue-400' 
                                : 'bg-purple-500/20 text-purple-400'
                            }`}>
                              {conv.conversion_type === 'signup' ? t('affiliate.dashboard.conversions.signup') : t('affiliate.dashboard.conversions.upgrade')}
                              {conv.subscription_tier && ` (${conv.subscription_tier})`}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                            {formatCurrency(conv.commission_amount)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {conv.is_paid ? (
                              <span className="inline-flex items-center text-green-400">
                                <CheckCircleIcon className="h-4 w-4 mr-1" />
                                {t('affiliate.dashboard.conversions.paidStatus')}
                              </span>
                            ) : (
                              <span className="inline-flex items-center text-amber-400">
                                <ClockIcon className="h-4 w-4 mr-1" />
                                {t('affiliate.dashboard.conversions.pendingStatus')}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                      {conversions?.conversions?.length === 0 && (
                        <tr>
                          <td colSpan="5" className="px-6 py-12 text-center text-gray-400">
                            {t('affiliate.dashboard.conversions.noConversionsFound')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Payouts Tab */}
        {activeTab === 'payouts' && (
          <div className="space-y-6">
            {/* Total Paid Card */}
            <div className="bg-gradient-to-r from-green-500/20 to-emerald-600/20 backdrop-blur-lg rounded-xl border border-green-500/30 p-6">
              <p className="text-green-300">{t('affiliate.dashboard.payouts.totalPaidOut')}</p>
              <p className="text-3xl font-bold text-white">{formatCurrency(payouts?.total_paid)}</p>
            </div>

            {/* Payouts Table */}
            <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 overflow-hidden">
              <div className="p-6 border-b border-white/10">
                <h3 className="text-lg font-semibold text-white">{t('affiliate.dashboard.payouts.payoutHistory')}</h3>
              </div>
              
              {tabLoading ? (
                <div className="p-12 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-400 mx-auto"></div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-white/10">
                    <thead className="bg-white/5">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">{t('affiliate.dashboard.payouts.date')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">{t('affiliate.dashboard.payouts.amount')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">{t('affiliate.dashboard.payouts.method')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">{t('affiliate.dashboard.payouts.reference')}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">{t('affiliate.dashboard.payouts.notes')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                      {payouts?.payouts?.map((payout) => (
                        <tr key={payout.id} className="hover:bg-white/5">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                            {formatDate(payout.payout_date)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-400">
                            {formatCurrency(payout.amount)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                            {payout.payment_method || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-400">
                            {payout.transaction_reference || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-400 max-w-xs truncate">
                            {payout.notes || '-'}
                          </td>
                        </tr>
                      ))}
                      {payouts?.payouts?.length === 0 && (
                        <tr>
                          <td colSpan="5" className="px-6 py-12 text-center text-gray-400">
                            {t('affiliate.dashboard.payouts.noPayoutsYet')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Link Builder Tab */}
        {activeTab === 'links' && (
          <div className="max-w-2xl mx-auto space-y-6">
            <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 p-6">
              <h3 className="text-lg font-semibold text-white mb-6">{t('affiliate.dashboard.linkBuilder.title')}</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    {t('affiliate.dashboard.linkBuilder.landingPage')}
                    <span className="text-gray-500 font-normal ml-1">{t('affiliate.dashboard.linkBuilder.whereToRedirect')}</span>
                  </label>
                  <select
                    value={linkBuilder.page}
                    onChange={(e) => setLinkBuilder({ ...linkBuilder, page: e.target.value })}
                    className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-4 py-3 focus:ring-2 focus:ring-pink-500"
                  >
                    <option value="home" className="bg-gray-900">{t('affiliate.dashboard.linkBuilder.homepage')}</option>
                    <option value="register" className="bg-gray-900">{t('affiliate.dashboard.linkBuilder.registerPage')}</option>
                    <option value="login" className="bg-gray-900">{t('affiliate.dashboard.linkBuilder.loginPage')}</option>
                    <option value="pricing" className="bg-gray-900">{t('affiliate.dashboard.linkBuilder.pricingPage')}</option>
                  </select>
                  <p className="mt-1 text-xs text-pink-400">
                    💡 {t('affiliate.dashboard.linkBuilder.homepageRecommendation')}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    {t('affiliate.dashboard.linkBuilder.sub1')}
                    <span className="text-gray-500 font-normal ml-1">{t('affiliate.dashboard.linkBuilder.sub1Desc')}</span>
                  </label>
                  <input
                    type="text"
                    value={linkBuilder.sub1}
                    onChange={(e) => setLinkBuilder({ ...linkBuilder, sub1: e.target.value })}
                    placeholder={t('affiliate.dashboard.linkBuilder.sub1Placeholder')}
                    className="w-full bg-white/10 border border-white/20 text-white placeholder-gray-400 rounded-lg px-4 py-3 focus:ring-2 focus:ring-pink-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    {t('affiliate.dashboard.linkBuilder.sub2')}
                    <span className="text-gray-500 font-normal ml-1">{t('affiliate.dashboard.linkBuilder.sub2Desc')}</span>
                  </label>
                  <input
                    type="text"
                    value={linkBuilder.sub2}
                    onChange={(e) => setLinkBuilder({ ...linkBuilder, sub2: e.target.value })}
                    placeholder={t('affiliate.dashboard.linkBuilder.sub2Placeholder')}
                    className="w-full bg-white/10 border border-white/20 text-white placeholder-gray-400 rounded-lg px-4 py-3 focus:ring-2 focus:ring-pink-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    {t('affiliate.dashboard.linkBuilder.sub3')}
                    <span className="text-gray-500 font-normal ml-1">{t('affiliate.dashboard.linkBuilder.sub3Desc')}</span>
                  </label>
                  <input
                    type="text"
                    value={linkBuilder.sub3}
                    onChange={(e) => setLinkBuilder({ ...linkBuilder, sub3: e.target.value })}
                    placeholder={t('affiliate.dashboard.linkBuilder.sub3Placeholder')}
                    className="w-full bg-white/10 border border-white/20 text-white placeholder-gray-400 rounded-lg px-4 py-3 focus:ring-2 focus:ring-pink-500"
                  />
                </div>
                
                <button
                  onClick={buildCustomLink}
                  className="w-full px-4 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-medium rounded-lg hover:from-pink-600 hover:to-purple-700 transition-all"
                >
                  {t('affiliate.dashboard.linkBuilder.generateLink')}
                </button>
              </div>
            </div>

            {/* Generated Link */}
            {builtLink && (
              <div className="bg-gradient-to-r from-pink-500/20 to-purple-600/20 backdrop-blur-lg rounded-xl border border-pink-500/30 p-6">
                <h4 className="font-semibold text-white mb-2">{t('affiliate.dashboard.linkBuilder.yourCustomLink')}</h4>
                <div className="flex items-center bg-white/10 rounded-lg p-3 border border-white/10">
                  <input
                    type="text"
                    value={builtLink.full_url}
                    readOnly
                    className="flex-1 bg-transparent text-white font-mono text-sm focus:outline-none"
                  />
                  <button
                    onClick={() => copyToClipboard(builtLink.full_url)}
                    className="ml-2 p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-white"
                  >
                    <ClipboardDocumentIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}

            {/* Quick Links */}
            {affiliateLink && (
              <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 p-6">
                <h4 className="font-semibold text-white mb-4">{t('affiliate.dashboard.linkBuilder.quickCopy')}</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/10">
                    <span className="text-sm text-gray-300">{t('affiliate.dashboard.linkBuilder.baseLink')}</span>
                    <button
                      onClick={() => copyToClipboard(affiliateLink.link)}
                      className="text-pink-400 hover:text-pink-300 text-sm font-medium"
                    >
                      {t('affiliate.dashboard.linkBuilder.copy')}
                    </button>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/10">
                    <span className="text-sm text-gray-300">{t('affiliate.dashboard.linkBuilder.affiliateCode')}</span>
                    <button
                      onClick={() => copyToClipboard(affiliateLink.affiliate_code)}
                      className="text-pink-400 hover:text-pink-300 text-sm font-medium"
                    >
                      {t('affiliate.dashboard.linkBuilder.copy')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, subtitle, color }) => {
  const colorClasses = {
    blue: 'bg-blue-500/20 text-blue-400',
    green: 'bg-green-500/20 text-green-400',
    purple: 'bg-purple-500/20 text-purple-400',
    pink: 'bg-pink-500/20 text-pink-400',
  };

  return (
    <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 p-6 hover:bg-white/15 transition-colors">
      <div className="flex items-center">
        <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
          <Icon className="h-6 w-6" />
        </div>
        <div className="ml-4">
          <p className="text-sm text-gray-400">{label}</p>
          <p className="text-2xl font-bold text-white">{value}</p>
          {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
};

// Funnel Step Component
const FunnelStep = ({ label, value, color }) => {
  const colorClasses = {
    blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    green: 'bg-green-500/20 text-green-400 border-green-500/30',
    purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  };

  return (
    <div className={`flex-1 text-center p-4 rounded-xl border backdrop-blur-sm ${colorClasses[color]}`}>
      <p className="text-2xl font-bold text-white">{value.toLocaleString()}</p>
      <p className="text-sm">{label}</p>
    </div>
  );
};

// Funnel Arrow Component
const FunnelArrow = ({ rate }) => (
  <div className="flex flex-col items-center px-4">
    <span className="text-xs text-gray-400 mb-1">{rate}</span>
    <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  </div>
);

export default AffiliateDashboard;
